import { useState, useEffect } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";

const GET_CONTRACT = gql`
  query MissionAdminContract($contractId: Int!) {
    missionAdminContract(contractId: $contractId) {
      id
      title
      brand {
        id
        name
      }
      totalBudget
      startAt
      endAt
      targetRevenue
      feeRate
      monthlyParticipationLimit
      status
      missionTargets {
        id
        missionType
        targetCount
      }
    }
  }
`;

const GET_BRANDS = gql`
  query MissionAdminBrandsForEdit {
    missionAdminBrands {
      id
      name
    }
  }
`;

const UPDATE_CONTRACT = gql`
  mutation MissionAdminUpdateContract(
    $contractId: Int!
    $input: UpdateContractInput!
  ) {
    missionAdminUpdateContract(contractId: $contractId, input: $input) {
      id
      title
      brand {
        name
      }
      totalBudget
      startAt
      endAt
      status
      monthlyParticipationLimit
    }
  }
`;

interface MissionTarget {
  id: string; // 임시 ID (추가/삭제용)
  missionType: string;
  targetCount: string;
}

const MissionAdminContractsEdit = () => {
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const contractIdNum = contractId ? parseInt(contractId, 10) : 0;

  // 디버깅용 로그
  useEffect(() => {
    console.log("contractId from URL:", contractId);
    console.log("contractIdNum parsed:", contractIdNum);
  }, [contractId, contractIdNum]);

  const [formData, setFormData] = useState({
    brandId: "",
    title: "",
    totalBudget: "",
    startAt: "",
    endAt: "",
    targetRevenue: "",
    feeRate: "",
    monthlyParticipationLimit: "",
  });
  const [missionTargets, setMissionTargets] = useState<MissionTarget[]>([]);

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
  } = useQuery(GET_CONTRACT, {
    variables: { contractId: contractIdNum },
    skip: !contractIdNum,
    onCompleted: (data) => {
      if (data?.missionAdminContract) {
        const contract = data.missionAdminContract;
        const startDate = new Date(contract.startAt);
        const endDate = new Date(contract.endAt);

        setFormData({
          brandId: contract.brand.id.toString(),
          title: contract.title,
          totalBudget: contract.totalBudget.toString(),
          startAt: startDate.toISOString().split("T")[0],
          endAt: endDate.toISOString().split("T")[0],
          targetRevenue: contract.targetRevenue?.toString() || "",
          feeRate: contract.feeRate
            ? (parseFloat(contract.feeRate) * 100).toString()
            : "",
          monthlyParticipationLimit:
            contract.monthlyParticipationLimit?.toString() || "",
        });

        if (contract.missionTargets && contract.missionTargets.length > 0) {
          setMissionTargets(
            contract.missionTargets.map((target: any) => ({
              id: target.id.toString(),
              missionType: target.missionType,
              targetCount: target.targetCount.toString(),
            }))
          );
        }
      }
    },
  });

  const { data: brandsData, loading: brandsLoading } = useQuery(GET_BRANDS);
  const [updateContract, { loading: updating }] = useMutation(UPDATE_CONTRACT, {
    onCompleted: () => {
      alert("계약이 수정되었습니다.");
      navigate("/ops/mission/contracts/connect");
    },
    onError: (error) => {
      alert(`에러: ${error.message}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.brandId ||
      !formData.title ||
      !formData.totalBudget ||
      !formData.startAt ||
      !formData.endAt
    ) {
      alert("필수 필드를 모두 입력해주세요.");
      return;
    }

    // 날짜만 선택했으므로 시간을 추가: 시작일은 00:00:00, 종료일은 15:00:00
    const startDate = new Date(formData.startAt);
    startDate.setHours(0, 0, 0, 0);
    const startAtISO = startDate.toISOString();

    const endDate = new Date(formData.endAt);
    endDate.setHours(15, 0, 0, 0);
    const endAtISO = endDate.toISOString();

    if (startDate >= endDate) {
      alert("시작일은 종료일보다 이전이어야 합니다.");
      return;
    }

    // missionTargets를 필터링하여 유효한 것만 전송
    const validMissionTargets = missionTargets
      .filter(
        (target) =>
          target.missionType &&
          target.targetCount &&
          parseInt(target.targetCount) > 0
      )
      .map((target) => ({
        missionType: target.missionType,
        targetCount: parseInt(target.targetCount),
      }));

    // 유효성 검증: monthlyParticipationLimit가 1 미만이면 에러
    if (
      formData.monthlyParticipationLimit &&
      parseInt(formData.monthlyParticipationLimit) < 1
    ) {
      alert("매월 참여 가능 횟수는 1 이상이어야 합니다.");
      return;
    }

    try {
      await updateContract({
        variables: {
          contractId: contractIdNum,
          input: {
            title: formData.title,
            totalBudget: parseInt(formData.totalBudget),
            startAt: startAtISO,
            endAt: endAtISO,
            targetRevenue: formData.targetRevenue
              ? parseInt(formData.targetRevenue)
              : null,
            monthlyParticipationLimit: formData.monthlyParticipationLimit
              ? parseInt(formData.monthlyParticipationLimit)
              : null,
            missionTargets:
              validMissionTargets.length > 0 ? validMissionTargets : undefined,
          },
        },
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";
      alert(`에러: ${message}`);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addMissionTarget = () => {
    setMissionTargets([
      ...missionTargets,
      {
        id: Date.now().toString(),
        missionType: "",
        targetCount: "",
      },
    ]);
  };

  const removeMissionTarget = (id: string) => {
    setMissionTargets(missionTargets.filter((target) => target.id !== id));
  };

  const updateMissionTarget = (
    id: string,
    field: "missionType" | "targetCount",
    value: string
  ) => {
    setMissionTargets(
      missionTargets.map((target) =>
        target.id === id ? { ...target, [field]: value } : target
      )
    );
  };

  const getMissionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      SEARCH: "검색",
      LIKE: "찜",
      ADD_TO_CART: "장바구니",
      PURCHASE: "구매",
      REVIEW: "리뷰",
      PHOTO_REVIEW: "포토리뷰",
      USAGE_SHOT: "사용샷 인증",
      ETC: "기타",
    };
    return labels[type] || type;
  };

  const availableMissionTypes = [
    "SEARCH",
    "LIKE",
    "ADD_TO_CART",
    "PURCHASE",
    "REVIEW",
    "PHOTO_REVIEW",
    "USAGE_SHOT",
    "ETC",
  ];

  // 이미 선택된 미션 타입 제외
  const getAvailableMissionTypes = () => {
    const usedTypes = new Set(
      missionTargets.map((t) => t.missionType).filter(Boolean)
    );
    return availableMissionTypes.filter((type) => !usedTypes.has(type));
  };

  if (contractLoading || brandsLoading) {
    return <div className="p-8">로딩 중...</div>;
  }

  if (contractError) {
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">
          에러가 발생했습니다: {contractError.message}
        </div>
        <button
          onClick={() => navigate("/ops/mission/contracts/connect")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  if (!contractIdNum) {
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">잘못된 계약 ID입니다.</div>
        <button
          onClick={() => navigate("/ops/mission/contracts/connect")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  if (!contractData?.missionAdminContract) {
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">
          컨트랙트를 찾을 수 없습니다. (ID: {contractIdNum})
        </div>
        <button
          onClick={() => navigate("/ops/mission/contracts/connect")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">계약 수정</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 브랜드 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            브랜드 <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.brandId}
            onChange={(e) => handleChange("brandId", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
            disabled={brandsLoading}
          >
            <option value="">브랜드 선택</option>
            {brandsData?.missionAdminBrands?.map((brand: any) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        {/* 제목 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            제목 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="예: 2025년 3월 네이버 성장 캠페인"
            required
          />
        </div>

        {/* 총 예산 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            총 예산 (원) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={formData.totalBudget}
            onChange={(e) => handleChange("totalBudget", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="1000000"
            required
            min="0"
          />
        </div>

        {/* 시작일 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            시작일 <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 ml-2">
              (시간은 자동으로 00:00:00으로 설정됩니다)
            </span>
          </label>
          <input
            type="date"
            value={formData.startAt}
            onChange={(e) => handleChange("startAt", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>

        {/* 종료일 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            종료일 <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 ml-2">
              (시간은 자동으로 15:00:00으로 설정됩니다)
            </span>
          </label>
          <input
            type="date"
            value={formData.endAt}
            onChange={(e) => handleChange("endAt", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>

        {/* 목표 매출 */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4">목표 매출 (선택사항)</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              목표 매출 (원)
            </label>
            <input
              type="number"
              value={formData.targetRevenue}
              onChange={(e) => handleChange("targetRevenue", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="예: 10000000"
              min="0"
            />
          </div>
        </div>

        {/* 매월 참여 가능 횟수 */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4">
            매월 참여 가능 횟수 (선택사항)
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              매월 참여 가능 횟수
              <span className="text-xs text-gray-500 ml-2">
                (빈 값 = 제한 없음, 각 월별로 독립적으로 카운트)
              </span>
            </label>
            <input
              type="number"
              value={formData.monthlyParticipationLimit}
              onChange={(e) =>
                handleChange("monthlyParticipationLimit", e.target.value)
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="예: 1 (매월 1회만 참여 가능)"
              min="1"
            />
            {formData.monthlyParticipationLimit &&
              parseInt(formData.monthlyParticipationLimit) < 1 && (
                <p className="mt-1 text-sm text-red-500">
                  1 이상의 값을 입력해주세요.
                </p>
              )}
            {formData.monthlyParticipationLimit && (
              <p className="mt-1 text-sm text-gray-500">
                예: 매월 {formData.monthlyParticipationLimit}회 참여 가능
              </p>
            )}
          </div>
        </div>

        {/* 미션 타입별 목표 */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              미션 타입별 목표 (선택사항)
            </h2>
            <button
              type="button"
              onClick={addMissionTarget}
              disabled={getAvailableMissionTypes().length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              + 목표 추가
            </button>
          </div>

          {missionTargets.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              목표를 추가하려면 위의 "목표 추가" 버튼을 클릭하세요.
            </p>
          ) : (
            <div className="space-y-4">
              {missionTargets.map((target) => (
                <div
                  key={target.id}
                  className="flex gap-4 items-end p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      미션 타입
                    </label>
                    <select
                      value={target.missionType}
                      onChange={(e) =>
                        updateMissionTarget(
                          target.id,
                          "missionType",
                          e.target.value
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">선택하세요</option>
                      {availableMissionTypes.map((type) => {
                        // 이미 다른 타겟에서 선택된 타입은 제외 (현재 타겟 제외)
                        const isUsed = missionTargets.some(
                          (t) => t.id !== target.id && t.missionType === type
                        );
                        if (isUsed) return null;
                        return (
                          <option key={type} value={type}>
                            {getMissionTypeLabel(type)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      목표 횟수
                    </label>
                    <input
                      type="number"
                      value={target.targetCount}
                      onChange={(e) =>
                        updateMissionTarget(
                          target.id,
                          "targetCount",
                          e.target.value
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="예: 100"
                      min="1"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMissionTarget(target.id)}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate("/ops/mission/contracts/connect")}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={updating}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {updating ? "수정 중..." : "수정"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MissionAdminContractsEdit;
